import { Rect } from '../lib/region';
import { sortByIndex } from '../render/utils/node';
import { TYPES } from '../render/constants';
import { Action } from './action';

function condition() {
  return true;
}

export const moveUp = new Action({
  name: 'Move up',
  handler: (store) => moveNodeByType(store, 'top'),
  shortcuts: 'ARROWUP',
  condition: condition,
});

export const moveDown = new Action({
  name: 'Move down',
  handler: (store) => moveNodeByType(store, 'bottom'),
  shortcuts: 'ARROWDOWN',
  condition: condition,
});

export const moveLeft = new Action({
  name: 'Move left',
  handler: (store) => moveNodeByType(store, 'left'),
  shortcuts: 'ARROWLEFT',
  condition: condition,
});

export const moveRight = new Action({
  name: 'Move right',
  handler: (store) => moveNodeByType(store, 'right'),
  shortcuts: 'ARROWRIGHT',
  condition: condition,
});

export const moveUpShift = new Action({
  name: 'Move up (Shift)',
  handler: (store) => moveNodeByType(store, 'top'),
  shortcuts: 'Shift+ARROWUP',
  condition: condition,
});

export const moveDownShift = new Action({
  name: 'Move down (Shift)',
  handler: (store) => moveNodeByType(store, 'bottom'),
  shortcuts: 'Shift+ARROWDOWN',
  condition: condition,
});

export const moveLeftShift = new Action({
  name: 'Move left (Shift)',
  handler: (store) => moveNodeByType(store, 'left'),
  shortcuts: 'Shift+ARROWLEFT',
  condition: condition,
});

export const moveRightShift = new Action({
  name: 'Move right (Shift)',
  handler: (store) => moveNodeByType(store, 'right'),
  shortcuts: 'Shift+ARROWRIGHT',
  condition: condition,
});

function moveNodeByType(store, type) {
  let move = null;
  const selectedNodes = store.api.getSelectedNodes();
  switch (type) {
    case 'top':
      move = { x: 0, y: -1 };
      break;
    case 'bottom':
      move = { x: 0, y: 1 };
      break;
    case 'left':
      move = { x: -1, y: 0 };
      break;
    case 'right':
      move = { x: 1, y: 0 };
      break;
  }
  const movement = store.providers.action.isShiftKeyPressed ? 10 : 1;
  moveNodeAction(store, selectedNodes, { x: move.x * movement, y: move.y * movement });
}

function moveNodeAction(store, nodes, movement) {
  nodes.forEach((node) => {
    store.api.updateNode(node.id, {
      x: Math.round(node.x + movement.x || 0),
      y: Math.round(node.y + movement.y || 0),
    });
  });
}

function matchFrameTarget(hitTestContext, frames, movingNodes) {
  let moveInTargetNode = null;
  const stageContext = hitTestContext.stageContext;
  for (let i = frames.length - 1; i >= 0; i--) {
    const node = frames[i];
    const nodeBox = stageContext.api.getNodeBox(node);
    if (movingNodes.find((n) => n.id === node.id)) continue;
    if (node.visible === false) continue;
    if (node.hidden) continue;
    if (stageContext.api.isNodeLocked(node)) continue;
    // 检查鼠标是否在容器内（移入判断）
    const isMouseInside = nodeBox.contains(hitTestContext.point.x, hitTestContext.point.y);
    if (isMouseInside) {
      moveInTargetNode = node;
      break;
    }
  }
  return moveInTargetNode;
}

export function autoMoveInto(renderContext, node) {
  const provider = {
    pointStart: {
      x: node.x,
      y: node.y,
    },
    getMovingNodeInitialBox: (id) => {
      const node = renderContext.api.getNode(id);
      provider.cacheMovingNode = provider.cacheMovingNode || {};
      const exsit = provider.cacheMovingNode[id];
      if (exsit) return exsit;
      const cache = {
        ...node,
      };
      provider.cacheMovingNode[id] = cache;
      return cache;
    },
  };

  const hitTestContext = {
    stageContext: renderContext,
    point: {
      x: node.x,
      y: node.y,
    },
    provider,
  };

  moveInOrOut(hitTestContext, [{ node }]);
}

export function moveInOrOut(hitTestContext, nodes, frames, types = [TYPES.frame]) {
  const stageContext = hitTestContext.stageContext;
  frames =
    frames ||
    stageContext.api
      .getNodes()
      .filter((node) => types.includes(node.type))
      .filter((node) => !nodes.find((n) => n.id === node.id))
      .sort(sortByIndex);

  const target = matchFrameTarget(hitTestContext, frames, nodes);
  const provider = hitTestContext.provider;

  // 移入：当鼠标在目标容器内时
  if (target) {
    const targetBox = stageContext.api.getNodeBox(target);
    const targetX = targetBox.x;
    const targetY = targetBox.y;

    const childrenNodes = stageContext.api.getChildren(target.id);
    const lastChild = childrenNodes[childrenNodes.length - 1];
    let lastIndex = lastChild?.index ?? target.index;
    const effectsGroups = [];

    nodes.forEach((cacheNode) => {
      const node = cacheNode.node;
      if (target.id === node.parentId) return;
      if (node.parentId === target.id) return;
      if (node.id === target.parentId) return;
      const parentNode = stageContext.api.getNode(node.parentId);
      if (parentNode && parentNode.type === TYPES.group) {
        const frame = stageContext.api.getFrameOf(node.id);
        if (frame.id === target.id) {
          return;
        }
      }

      const box = stageContext.api.getNodeBox(node);
      let deltaX = 0;
      let deltaY = 0;

      const deltaW = node.w > 0 ? 0 : -node.w;
      const deltaH = node.h > 0 ? 0 : -node.h;
      deltaX = box.x - node.x + deltaW;
      deltaY = box.y - node.y + deltaH;
      deltaX -= targetX;
      deltaY -= targetY;

      let moveX = 0;
      let moveY = 0;
      if (provider.pointStart) {
        moveX = hitTestContext.point.x - provider.pointStart.x;
        moveY = hitTestContext.point.y - provider.pointStart.y;
      }

      const cache = provider.getMovingNodeInitialBox(node.id);

      cache.x += deltaX;
      cache.y += deltaY;

      lastIndex = stageContext.api.getNextIndex(lastIndex);

      const patch = {
        parentId: target.id,
        x: cache.x + moveX,
        y: cache.y + moveY,
        index: lastIndex,
      };

      if (provider.moveOutCache) {
        const cache = provider.moveOutCache[node.id];
        if (cache && cache.parentId === target.id) {
          patch.index = cache.index;
        }
      }

      hitTestContext.stageContext.api.updateNode(node.id, patch);

      if (parentNode && parentNode.type === TYPES.group) {
        effectsGroups.push(parentNode);
      }
    });

    return effectsGroups;
  } else {
    // 移出: 检查移动中的节点是否完全移出了当前容器
    let shouldMoveOut = false;
    const effectsGroups = [];
    const nodesToCheckMoveOut = nodes.filter((boxCache) => boxCache.node.parentId);
    for (const boxCache of nodesToCheckMoveOut) {
      const node = boxCache.node;
      const container = stageContext.api.getNode(node.parentId);
      if (!container) continue;

      const frame = stageContext.api.getFrameOf(node.id);
      const containerBox = stageContext.api.getNodeBox(container);
      const frameBox = stageContext.api.getNodeBox(frame);
      const nodeBox = stageContext.api.getNodeBox(node);
      // 使用 Rect 简化面积计算
      const containerRect = new Rect(
        containerBox.x,
        containerBox.y,
        containerBox.w,
        containerBox.h
      );
      const frameRect = frameBox ? Rect.from(frameBox) : null;
      const nodeRect = new Rect(nodeBox.x, nodeBox.y, nodeBox.w, nodeBox.h);
      const intersection = containerRect.intersection(nodeRect);

      if (frameRect && !frameRect.intersection(nodeRect)) {
        shouldMoveOut = true;
        break;
      }

      // 只有当节点完全移出容器时才算移出（完全不交叉）
      if (!intersection) {
        shouldMoveOut = true;
        break;
      }
    }

    if (shouldMoveOut) {
      // 移出：当节点与容器完全不交叉时
      provider.moveOutCache = provider.moveOutCache || {};
      stageContext.api.transact(() => {
        let lastIndex = null;
        nodes
          .filter((boxCache) => boxCache.node.parentId)
          .forEach((boxCache) => {
            const node = boxCache.node;
            const container = stageContext.api.getNode(node.parentId);
            if (!container) return;
            if (container.type === TYPES.group) {
              effectsGroups.push(container);
            }

            const nodeBox = stageContext.api.getNodeBox(node);
            const deltaW = node.w > 0 ? 0 : -node.w;
            const deltaH = node.h > 0 ? 0 : -node.h;
            const deltaX = nodeBox.x - node.x + deltaW;
            const deltaY = nodeBox.y - node.y + deltaH;

            const cache = provider.getMovingNodeInitialBox(node.id);

            cache.x += deltaX;
            cache.y += deltaY;

            const moveX = hitTestContext.point.x - provider.pointStart.x;
            const moveY = hitTestContext.point.y - provider.pointStart.y;
            lastIndex = stageContext.api.getNextIndex(container.index);

            const patch = {
              parentId: null,
              x: cache.x + moveX,
              y: cache.y + moveY,
              index: lastIndex,
            };
            provider.moveOutCache[node.id] = {
              index: node.index,
              parentId: node.parentId,
            };
            stageContext.api.updateNode(node.id, patch);
          });
      });
    }

    return effectsGroups;
  }
}
